import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  PssBoostGaugeInfo,
  PssPlayerDetails,
  PssRushTarget,
  PssAuthService,
  PssSession,
  RedeemCodeRecord,
  RedeemCodeResult,
  RedeemCodeReward
} from '../../../services/pss-auth.service';
import { spriteIdOrEmpty } from '../pss-display-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

const BOOST_CAP_SECONDS = 12 * 3600;

@Component({
  selector: 'app-overview-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSpriteComponent],
  templateUrl: './overview-tab.component.html',
  styleUrls: ['./overview-tab.component.css']
})
export class OverviewTabComponent implements OnInit, OnDestroy, OnChanges {
  @Input() details!: PssPlayerDetails;
  @Input() session: PssSession | null = null;
  @Input() enableBoostUi = false;
  @Input() boostGauge: PssBoostGaugeInfo | null = null;
  @Input() rushTargets: PssRushTarget[] = [];
  @Output() useBoost = new EventEmitter<void>();

  showResources = true;
  showTrophies  = true;
  showPvp       = true;

  redeemCodes: RedeemCodeRecord[] = [];
  selectedRedeemCode = '';
  redeemInput = '';
  redeemMessage = '';
  redeemLoading = false;
  showRedeemModal = false;

  credentialSyncLoading = false;
  credentialSyncMessage = '';
  credentialSyncError = '';

  displaySeconds = 0;
  private ticker: ReturnType<typeof setInterval> | null = null;

  constructor(private auth: PssAuthService) {}

  ngOnInit(): void {
    this.resetDisplaySeconds();
    this.startTicker();
    this.loadRedeemCodes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['boostGauge']) {
      this.resetDisplaySeconds();
    }
  }

  ngOnDestroy(): void {
    this.stopTicker();
  }

  private resetDisplaySeconds(): void {
    this.displaySeconds = this.boostGauge?.boostAmountSeconds ?? 0;
  }

  private startTicker(): void {
    this.stopTicker();
    this.ticker = setInterval(() => {
      if (this.displaySeconds < BOOST_CAP_SECONDS) {
        this.displaySeconds = Math.min(BOOST_CAP_SECONDS, this.displaySeconds + 1);
      }
    }, 1000);
  }

  private stopTicker(): void {
    if (this.ticker !== null) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
  }

  loadRedeemCodes(): void {
    this.auth.listRedeemCodes('active').subscribe({
      next: codes => {
        this.redeemCodes = codes;
        if (!this.selectedRedeemCode && codes.length) {
          this.selectedRedeemCode = codes[0].code;
          this.redeemInput = codes[0].code;
        }
      },
      error: err => {
        this.redeemMessage = err?.message || 'Could not load redeem codes';
      }
    });
  }

  onRedeemCodeSelect(code: string): void {
    this.selectedRedeemCode = code;
    this.redeemInput = code;
    this.redeemMessage = '';
  }

  redeem(): void {
    const code = (this.redeemInput || this.selectedRedeemCode || '').trim().toUpperCase();
    if (!code) {
      this.redeemMessage = 'Please enter or select a redeem code.';
      return;
    }
    this.redeemLoading = true;
    this.auth.redeemCode(code).subscribe({
      next: (result: RedeemCodeResult) => {
        if (result.status === 'expired') {
          this.redeemMessage = `Code ${code} is expired; removed from active list.`;
          this.selectedRedeemCode = '';
          this.redeemInput = '';
          this.loadRedeemCodes();
          this.redeemLoading = false;
          return;
        }
        if (result.status === 'already_redeemed') {
          this.redeemMessage = `You've already redeemed ${code} on this account.`;
          this.loadRedeemCodes();
          this.redeemLoading = false;
          return;
        }

        this.redeemMessage = result.errorMessage
          ? `Redeem failed: ${result.errorMessage}${result.errorCode ? ` (errorCode: ${result.errorCode})` : ''}`
          : `${code} redeemed! Items have been added to your account — check your inventory in-game.`;

        this.loadRedeemCodes();
        this.redeemLoading = false;
      },
      error: err => {
        this.redeemMessage = err?.message || 'Redeem request failed';
        this.redeemLoading = false;
      }
    });
  }

  formatNumber(val: string): string {
    const n = parseInt(val, 10);
    if (isNaN(n)) return val;
    return n.toLocaleString();
  }

  winRate(wins: string, losses: string): string {
    const w = parseInt(wins, 10);
    const l = parseInt(losses, 10);
    if (isNaN(w) || isNaN(l) || w + l === 0) return '—';
    return Math.round((w / (w + l)) * 100) + '%';
  }

  formatHhMm(totalSeconds: number): string {
    const safe = Math.max(0, totalSeconds | 0);
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  formatHhMmSs(totalSeconds: number): string {
    const safe = Math.max(0, totalSeconds | 0);
    const d = Math.floor(safe / 86400);
    const h = Math.floor((safe % 86400) / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;

    if (d > 0) {
      return `${d}d ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  boostGaugeProgress(): number {
    const seconds = this.boostGauge?.boostAmountSeconds ?? this.displaySeconds;
    const fraction = seconds / BOOST_CAP_SECONDS;
    return Math.min(1, Math.max(0, fraction));
  }

  get boostTarget(): PssRushTarget | null {
    return this.rushTargets && this.rushTargets.length ? this.rushTargets[0] : null;
  }

  get boostTargetSpriteId(): string {
    if (!this.boostTarget) return '';
    return spriteIdOrEmpty(this.boostTarget.logoSpriteId);
  }

  boostTargetName(): string {
    if (!this.boostTarget) return '';
    return this.boostTarget.label || `${this.boostTarget.targetType} upgrade`;
  }

  boostTargetTypeLabel(): string {
    if (!this.boostTarget) return '';
    if (this.boostTarget.targetType === 'ship') return 'Ship upgrade';
    if (this.boostTarget.targetType === 'room') return 'Room upgrade';
    if (this.boostTarget.targetType === 'research') return 'Research';
    return this.boostTarget.targetType;
  }

  boostTargetRemaining(): string {
    if (!this.boostTarget || this.boostTarget.remainingSeconds == null || this.boostTarget.remainingSeconds <= 0) return '';
    return this.formatHhMmSs(this.boostTarget.remainingSeconds);
  }

  triggerUseBoost(): void {
    this.useBoost.emit();
  }

  get isServiceAccount(): boolean {
    return !!this.session?.isServiceAccount;
  }

  get credentialsSavedOnServer(): boolean {
    return !!this.session?.hasDeviceKey && !!this.session?.hasRefreshToken;
  }

  syncServiceCredentials(): void {
    if (this.credentialSyncLoading) return;
    this.credentialSyncLoading = true;
    this.credentialSyncMessage = '';
    this.credentialSyncError = '';
    this.auth.syncServiceCredentials().subscribe({
      next: () => {
        this.credentialSyncLoading = false;
        this.credentialSyncMessage = 'Poller credentials synced.';
      },
      error: err => {
        this.credentialSyncLoading = false;
        this.credentialSyncError = err?.error?.error || err?.message || 'Credential sync failed';
      }
    });
  }
}
